import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/ui/BackButton';
import DocumentStatusBadge from '../document-management/components/DocumentStatusBadge';
import { documentService } from '../../services/documentService';
import { useAuth } from '../../contexts/AuthContext';

// Demo audit trail entries
const generateAuditTrail = (doc) => [
  {
    id: 1,
    action: 'Document uploaded',
    user: doc?.owner || 'System',
    timestamp: doc?.issue_date || doc?.issueDate || new Date()?.toISOString(),
    icon: 'Upload',
    color: 'text-accent'
  },
  {
    id: 2,
    action: 'Metadata updated',
    user: doc?.owner || 'Admin',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
    icon: 'Edit',
    color: 'text-warning'
  },
  {
    id: 3,
    action: 'Document viewed',
    user: 'Site Manager',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)?.toISOString(),
    icon: 'Eye',
    color: 'text-muted-foreground'
  }
];

const DocumentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, siteId, documentId } = useParams();
  const { userProfile } = useAuth();

  // Support both /documents/:id and /sites/:siteId/documents/:documentId
  const resolvedId = id || documentId;

  // Restore filter state passed via navigation state
  const passedDocument = location?.state?.document;
  const filterState = location?.state?.filterState;
  const openReplace = location?.state?.openReplace || false;
  const openEdit = location?.state?.openEdit || false;

  const [loading, setLoading] = useState(true);
  const [documentData, setDocumentData] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(openEdit);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState({});
  const replaceInputRef = React.useRef(null);

  useEffect(() => {
    loadDocument();
  }, [resolvedId]);

  // Auto-trigger replace file picker if navigated with openReplace flag
  useEffect(() => {
    if (openReplace && !loading && replaceInputRef?.current) {
      replaceInputRef?.current?.click();
    }
  }, [openReplace, loading]);

  const loadDocument = async () => {
    try {
      setLoading(true);

      // If document was passed via navigation state, use it immediately
      if (passedDocument) {
        setDocumentData(passedDocument);
        if (passedDocument?.file_path) {
          const urlResult = await documentService?.getDocumentFileUrl(passedDocument?.file_path);
          if (urlResult?.url) setFileUrl(urlResult?.url);
        }
        setLoading(false);
        return;
      }

      // Otherwise fetch from service
      if (resolvedId) {
        const result = await documentService?.getDocumentById(resolvedId);
        if (result?.data) {
          setDocumentData(result?.data);
          if (result?.data?.file_path) {
            const urlResult = await documentService?.getDocumentFileUrl(result?.data?.file_path);
            if (urlResult?.url) setFileUrl(urlResult?.url);
          }
        }
      }
    } catch (err) {
      console.error('Error loading document:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Build query string from saved filter state to restore filters
    if (filterState) {
      const params = new URLSearchParams();
      if (filterState?.searchQuery) params?.set('search', filterState?.searchQuery);
      if (filterState?.selectedSite && filterState?.selectedSite !== 'all') params?.set('siteId', filterState?.selectedSite);
      if (filterState?.selectedCategory && filterState?.selectedCategory !== 'all') params?.set('category', filterState?.selectedCategory);
      if (filterState?.selectedStatus && filterState?.selectedStatus !== 'all') params?.set('status', filterState?.selectedStatus);
      const qs = params?.toString();
      navigate(`/document-management${qs ? '?' + qs : ''}`);
    } else if (siteId) {
      navigate(`/sites/${siteId}`);
    } else {
      navigate('/document-management');
    }
  };

  const handleDownload = async () => {
    if (!fileUrl) return;
    try {
      setDownloading(true);
      const response = await fetch(fileUrl);
      const blob = await response?.blob();
      const url = window.URL?.createObjectURL(blob);
      const a = document?.createElement('a');
      a.href = url;
      a.download = documentData?.title || 'document';
      document?.body?.appendChild(a);
      a?.click();
      window.URL?.revokeObjectURL(url);
      document?.body?.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const result = await documentService?.replaceDocumentFile(
        file,
        resolvedId,
        documentData?.company_id,
        documentData?.site_id
      );
      if (result?.error) {
        setUploadError(result?.error?.message);
      } else {
        await loadDocument();
      }
    } catch (err) {
      console.error('[DocumentDetail] upload file failed:', err);
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleEditSave = async () => {
    try {
      const result = await documentService?.updateDocument(resolvedId, editForm);
      if (result?.data) {
        setDocumentData(prev => ({ ...prev, ...editForm }));
      }
    } catch (err) {
      console.error('Error updating document:', err);
    }
    setIsEditMode(false);
  };

  const handleDelete = async () => {
    try {
      await documentService?.deleteDocument(resolvedId);
      handleBack();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
    setShowDeleteConfirm(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return 'No date';
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return '—';
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getComputedStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'no-expiry', daysUntilExpiry: null };
    const date = new Date(expiryDate);
    if (isNaN(date?.getTime())) return { status: 'no-expiry', daysUntilExpiry: null };
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const diffMs = date - today;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { status: 'expired', daysUntilExpiry: diffDays };
    if (diffDays <= 30) return { status: 'expiring-soon', daysUntilExpiry: diffDays };
    return { status: 'valid', daysUntilExpiry: diffDays };
  };

  const getCategoryIcon = (category) => {
    const icons = { rams: 'FileText', insurance: 'Shield', training: 'GraduationCap', plant: 'Wrench' };
    return icons?.[category] || 'File';
  };

  const expiryDate = documentData?.expiry_date || documentData?.expiryDate;
  const { status, daysUntilExpiry } = getComputedStatus(expiryDate);
  const isPDF = documentData?.mime_type?.includes('pdf') || documentData?.file_path?.endsWith('.pdf');
  const isImage = documentData?.mime_type?.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i?.test(documentData?.file_path || '');
  const auditTrail = documentData ? generateAuditTrail(documentData) : [];
  const isAdmin = userProfile?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <Icon name="Loader2" size={48} className="text-accent animate-spin" />
        </div>
      </div>
    );
  }

  if (!documentData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <Icon name="FileX" size={48} className="text-muted-foreground" />
          <p className="text-lg font-medium text-foreground">Document not found</p>
          <Button variant="outline" iconName="ArrowLeft" onClick={handleBack}>Back to Documents</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{documentData?.title || 'Document Detail'} - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-12 px-4 lg:px-6 max-w-5xl mx-auto">
          {/* Back button */}
          <div className="mb-6">
            <BackButton fallback="/document-management" />
          </div>

          {/* Header card */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-xl flex-shrink-0">
                  <Icon name={getCategoryIcon(documentData?.category)} size={32} color="var(--color-accent)" />
                </div>
                <div>
                  {isEditMode ? (
                    <input
                      className="text-2xl font-bold text-foreground bg-muted border border-border rounded-lg px-3 py-1 mb-2 w-full"
                      value={editForm?.title ?? documentData?.title}
                      onChange={e => setEditForm(prev => ({ ...prev, title: e?.target?.value }))}
                    />
                  ) : (
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{documentData?.title}</h1>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <DocumentStatusBadge status={status} daysUntilExpiry={daysUntilExpiry} />
                    <span className="text-sm text-muted-foreground">
                      {daysUntilExpiry !== null
                        ? daysUntilExpiry < 0
                          ? `Expired ${Math.abs(daysUntilExpiry)} days ago`
                          : `${daysUntilExpiry} days remaining`
                        : 'No expiry date'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2">
                {fileUrl && (
                  <Button variant="outline" size="sm" iconName="Download" onClick={handleDownload} disabled={downloading}>
                    {downloading ? 'Downloading...' : 'Download'}
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <input
                      id="doc-file-input"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                      className="hidden"
                      ref={replaceInputRef}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      onClick={() => document?.getElementById('doc-file-input')?.click()}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Replace File'}
                    </Button>
                    {isEditMode ? (
                      <>
                        <Button size="sm" iconName="Check" onClick={handleEditSave}>Save</Button>
                        <Button variant="outline" size="sm" onClick={() => { setIsEditMode(false); setEditForm({}); }}>Cancel</Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Edit"
                        onClick={() => { setIsEditMode(true); setEditForm({ title: documentData?.title, notes: documentData?.notes }); }}
                      >
                        Edit Metadata
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Trash2"
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>

            {uploadError && (
              <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <p className="text-sm text-destructive">{uploadError}</p>
              </div>
            )}
          </div>

          {/* Metadata grid */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6 mb-6">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Info" size={18} className="text-accent" />
              Document Details
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Category</p>
                <p className="text-sm font-medium text-foreground capitalize">
                  {documentData?.categoryLabel || documentData?.category?.replace(/_/g, ' ')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Site</p>
                <p className="text-sm font-medium text-foreground">
                  {documentData?.siteName || documentData?.sites?.site_name || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Owner</p>
                <p className="text-sm font-medium text-foreground">{documentData?.owner || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Issue Date</p>
                <p className="text-sm font-medium text-foreground">
                  {formatDate(documentData?.issue_date || documentData?.issueDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Expiry Date</p>
                <p className="text-sm font-medium text-foreground">{formatDate(expiryDate)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <DocumentStatusBadge status={status} daysUntilExpiry={daysUntilExpiry} />
              </div>
              {documentData?.file_size && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">File Size</p>
                  <p className="text-sm font-medium text-foreground">
                    {(documentData?.file_size / 1024)?.toFixed(1)} KB
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              {isEditMode ? (
                <textarea
                  className="w-full text-sm text-foreground bg-muted border border-border rounded-lg px-3 py-2 resize-none"
                  rows={3}
                  placeholder="Add notes..."
                  value={editForm?.notes ?? documentData?.notes ?? ''}
                  onChange={e => setEditForm(prev => ({ ...prev, notes: e?.target?.value }))}
                />
              ) : (
                <p className="text-sm text-foreground">
                  {documentData?.notes || <span className="text-muted-foreground italic">No notes added</span>}
                </p>
              )}
            </div>
          </div>

          {/* File Preview */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6 mb-6">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Eye" size={18} className="text-accent" />
              File Preview
            </h2>
            {fileUrl ? (
              <div className="bg-muted/50 rounded-lg border border-border overflow-hidden">
                {isPDF && (
                  <iframe src={fileUrl} className="w-full h-[600px]" title="Document Preview" />
                )}
                {isImage && (
                  <img
                    src={fileUrl}
                    alt={documentData?.title}
                    className="w-full h-auto max-h-[600px] object-contain"
                  />
                )}
                {!isPDF && !isImage && (
                  <div className="p-12 text-center">
                    <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground mb-4">Preview not available for this file type</p>
                    <Button size="sm" iconName="Download" onClick={handleDownload} disabled={downloading}>
                      {downloading ? 'Downloading...' : 'Download File'}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-muted/50 rounded-lg p-12 border border-border text-center">
                <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h4 className="text-base font-semibold text-foreground mb-2">No file uploaded yet</h4>
                <p className="text-sm text-muted-foreground mb-4">This document does not have an associated file</p>
                {isAdmin && (
                  <Button
                    size="sm"
                    iconName="Upload"
                    onClick={() => document?.getElementById('doc-file-input')?.click()}
                  >
                    Upload File
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Upload History / Audit Trail */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="History" size={18} className="text-accent" />
              Upload History &amp; Audit Trail
            </h2>
            <div className="space-y-0">
              {auditTrail?.map((entry, idx) => (
                <div key={entry?.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full bg-muted flex-shrink-0 ${entry?.color}`}>
                      <Icon name={entry?.icon} size={14} />
                    </div>
                    {idx < auditTrail?.length - 1 && (
                      <div className="w-px flex-1 bg-border my-1" />
                    )}
                  </div>
                  <div className="pb-4 flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{entry?.action}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">{entry?.user}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">{formatDateTime(entry?.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border border-border shadow-elevation-3 p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-destructive/10 rounded-full">
                <Icon name="Trash2" size={20} className="text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete Document</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Are you sure you want to delete <strong>{documentData?.title}</strong>?
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button className="flex-1 bg-destructive hover:bg-destructive/90 text-white" onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentDetail;